import React, { useState, useEffect } from 'react';
import { useWorkerContext } from '../contexts/WorkerContext';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
} from '@mui/material';

interface QuarterData {
  opps_without_partner: number;
  opps_with_partner: number;
  coverage_percentage: number;
}

interface ManagerData {
  opportunity_owner__manager: string;
  quarterly_data: {
    [key: string]: QuarterData;
  };
}

interface ScoreboardData {
  quarters: string[];
  data: ManagerData[];
}

const getCoverageColor = (percentage: number) => {
  if (percentage >= 90) return '#4caf50'; // Green
  if (percentage >= 80) return '#ffeb3b'; // Yellow
  return '#f44336'; // Red
};

export default function Scoreboard() {
  const [scoreboardData, setScoreboardData] = useState<ScoreboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { worker } = useWorkerContext();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await worker.getScoreboard();
        setScoreboardData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [worker]);

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;
  if (!scoreboardData) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>Partner Coverage Scoreboard</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Includes only stage 2 and above opportunities
        </Typography>
      </Box>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Manager</TableCell>
              {scoreboardData.quarters.map((quarter) => (
                <TableCell key={quarter} align="center" colSpan={4}>
                  {quarter}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              <TableCell />
              {scoreboardData.quarters.map((quarter) => (
                <React.Fragment key={quarter}>
                  <TableCell align="right">Without Partner</TableCell>
                  <TableCell align="right">With Partner</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="right">Coverage %</TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {scoreboardData.data.map((row) => (
              <TableRow key={row.opportunity_owner__manager}>
                <TableCell>{row.opportunity_owner__manager}</TableCell>
                {scoreboardData.quarters.map((quarter) => {
                  const quarterData = row.quarterly_data[quarter] || {
                    opps_without_partner: 0,
                    opps_with_partner: 0,
                    coverage_percentage: 0,
                  };
                  const total = quarterData.opps_without_partner + quarterData.opps_with_partner;
                  
                  return (
                    <React.Fragment key={quarter}>
                      <TableCell align="right">{quarterData.opps_without_partner}</TableCell>
                      <TableCell align="right">{quarterData.opps_with_partner}</TableCell>
                      <TableCell align="right">{total}</TableCell>
                      <TableCell 
                        align="right"
                        sx={{
                          color: getCoverageColor(quarterData.coverage_percentage),
                          fontWeight: 'bold'
                        }}
                      >
                        {quarterData.coverage_percentage.toFixed(1)}%
                      </TableCell>
                    </React.Fragment>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
